package io.horion.cv.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronDown
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronUp
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.styles.AlignmentOptionIconStyle
import io.horion.cv.styles.AlignmentOptionStyle
import io.horion.cv.utils.Colors
import kotlinx.browser.document
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

@Composable
fun AlignmentDropdown(
    current: TextAlignment,
    onChange: (TextAlignment) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    var openUpwards by remember { mutableStateOf(false) }
    var openLeftwards by remember { mutableStateOf(false) }
    var triggerBottomPx by remember { mutableStateOf(0.0) }
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    DisposableEffect(open) {
        if (!open) return@DisposableEffect onDispose {}
        val clickListener: (Event) -> Unit = { event ->
            val target = (event as? MouseEvent)?.target as? HTMLElement
            val insideDropdown = target?.closest("[data-alignment-dropdown]") != null
            if (!insideDropdown) open = false
        }
        val scrollListener: (Event) -> Unit = {
            open = false
        }
        document.addEventListener("click", clickListener, true)
        document.addEventListener("scroll", scrollListener, true)
        onDispose {
            document.removeEventListener("click", clickListener, true)
            document.removeEventListener("scroll", scrollListener, true)
        }
    }

    Div(
        attrs = {
            attr("data-alignment-dropdown", "true")

            style {
                property("position", "relative")
                property("display", "inline-block")
                property("z-index", "10000")
            }
        }
    ) {
        Row(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .padding(leftRight = 6.px, topBottom = 4.px)
                .onClick { event ->
                    val rect = (event.target as? HTMLElement)
                        ?.closest("[data-alignment-dropdown]")
                        ?.getBoundingClientRect()

                    if (rect != null) {
                        val menuWidth = 180.0
                        val menuHeight = 180.0

                        val spaceBelow = kotlinx.browser.window.innerHeight - rect.bottom
                        val spaceAbove = rect.top

                        val spaceRight = kotlinx.browser.window.innerWidth - rect.right
                        val spaceLeft = rect.left

                        openUpwards =
                            spaceBelow < menuHeight && spaceAbove > spaceBelow

                        openLeftwards =
                            spaceRight < menuWidth && spaceLeft > spaceRight
                    }

                    open = !open
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            current.Icon(
                modifier = Modifier
                    .fontSize(16.px)
                    .color(if (isDark) Colors.White else Colors.Primary)
            )
            if (open) {
                FaChevronUp(
                    modifier = Modifier
                        .fontSize(14.px)
                        .margin(left = 10.px)
                        .color(if (isDark) Colors.White else Colors.Primary)
                )
            } else {
                FaChevronDown(
                    modifier = Modifier
                        .fontSize(14.px)
                        .margin(left = 10.px)
                        .color(if (isDark) Colors.White else Colors.Primary)
                )
            }
        }

        if (open) {
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .then(
                        if (openUpwards) {
                            Modifier.bottom(32.px)
                        } else {
                            Modifier.top(32.px)
                        }
                    )
                    .then(
                        if (openLeftwards) {
                            Modifier.styleModifier {
                                property("right", "0")
                            }
                        } else {
                            Modifier.styleModifier {
                                property("left", "0")
                            }
                        }
                    )
                    .backgroundColor(
                        if (isDark) Colors.DarkBG else Colors.LightGray
                    )
                    .borderRadius(8.px)
                    .padding(6.px)
                    .zIndex(10000)
                    .styleModifier {
                        property("width", "max-content")
                        property("min-width", "160px")
                        property("max-width", "calc(100vw - 16px)")
                        property("white-space", "nowrap")
                        property("box-sizing", "border-box")
                        property("overflow-x", "auto")
                    }
            ) {
                TextAlignment.entries.forEach { option ->
                    Row(
                        modifier = AlignmentOptionStyle.toModifier()
                            .fillMaxWidth()
                            .cursor(Cursor.Pointer)
                            .padding(6.px)
                            .borderRadius(6.px)
                            .onClick {
                                onChange(option)
                                exec(
                                    when (option) {
                                        TextAlignment.LEFT -> "Aligner à gauche"
                                        TextAlignment.RIGHT -> "Aligner à droite"
                                        TextAlignment.CENTER -> "Centrer"
                                        TextAlignment.JUSTIFY -> "Justifier"
                                    }
                                )
                                open = false
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (option == current) {
                            Image(
                                src = if (isDark) "/checkmark2.svg" else "checkmark1.svg",
                                modifier = Modifier.size(18.px)
                            )
                        } else {
                            Box(Modifier.width(12.px))
                        }
                        Box(Modifier.width(10.px))
                        option.Icon(
                            modifier = AlignmentOptionIconStyle.toModifier()
                                .color(if (isDark) Colors.White else Colors.Primary)
                        )
                        SpanText(
                            text = option.label,
                            modifier = Modifier
                                .fontSize(16.px)
                                .margin(left = 15.px)
                                .color(if (isDark) Colors.White else Colors.Primary)
                                .whiteSpace(WhiteSpace.NoWrap)
                        )
                    }
                }
            }
        }
    }
}